import { FilterState, DEFAULT_IMAGE_SIZE } from '../Schema/FilterState';
import { DateRangeFilter } from 'owa-files-filter-date-filter';
import { FileExtensionType, getFileExtensionData } from 'owa-files-filter-file-extensions-filter';
import IsEqualTo from 'owa-service/lib/contract/IsEqualTo';
import RestrictionType from 'owa-service/lib/contract/RestrictionType';
import SearchExpressionType from 'owa-service/lib/contract/SearchExpressionType';
import andType from 'owa-service/lib/factory/and';
import constantType from 'owa-service/lib/factory/constant';
import fieldURIOrConstantType from 'owa-service/lib/factory/fieldURIOrConstantType';
import isEqualType from 'owa-service/lib/factory/isEqualTo';
import isGreaterOrEqualType from 'owa-service/lib/factory/isGreaterThanOrEqualTo';
import isLessOrEqualType from 'owa-service/lib/factory/isLessThanOrEqualTo';
import isNotEqualType from 'owa-service/lib/factory/isNotEqualTo';
import orType from 'owa-service/lib/factory/or';
import propertyUri from 'owa-service/lib/factory/propertyUri';
import restrictionType from 'owa-service/lib/factory/restrictionType';

function configureFileSizeRestriction(minFileSize: number): SearchExpressionType {
    const greaterThanMinSizeFilter = isGreaterOrEqualType({
        Item: propertyUri({
            FieldURI: 'FileSize',
        }),
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constantType({
                Value: minFileSize.toString(),
            }),
        }),
    });

    // Ref attachments have size set to 0 bytes. For v1, We should include them in all cases.
    const refAttachmentsFilter = isNotEqualType({
        Item: propertyUri({
            FieldURI: 'FileStorageProviderContext',
        }),
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constantType({
                Value: '2', // Value 2 indicates classic attachment
            }),
        }),
    });

    return orType({
        Items: [greaterThanMinSizeFilter, refAttachmentsFilter],
    });
}

function configurePhotosOnlyRestriction(): SearchExpressionType {
    // TODO: Need to finalize the exif properties that can best filter camera photos (24267)
    return isGreaterOrEqualType({
        Item: propertyUri({
            FieldURI: 'ImageOrientation',
        }),
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constantType({
                Value: '1',
            }),
        }),
    });
}
function configureDateRestriction(dateRangeFilter: DateRangeFilter): SearchExpressionType {
    const dateFilters = [];

    const dateRange = dateRangeFilter.dateRange;
    if (dateRange.startDate) {
        dateFilters.push(
            isGreaterOrEqualType({
                Item: propertyUri({
                    FieldURI: 'DateTimeReceived',
                }),
                FieldURIOrConstant: fieldURIOrConstantType({
                    Item: constantType({
                        Value: dateRange.startDate.toISOString(),
                    }),
                }),
            })
        );
    }
    if (dateRange.endDate) {
        dateFilters.push(
            isLessOrEqualType({
                Item: propertyUri({
                    FieldURI: 'DateTimeReceived',
                }),
                FieldURIOrConstant: fieldURIOrConstantType({
                    Item: constantType({
                        Value: dateRange.endDate.toISOString(),
                    }),
                }),
            })
        );
    }
    return andType({
        Items: dateFilters,
    });
}

function configureExtensionsRestriction(
    selectedExtensionGroupFilters: Map<string, string>
): SearchExpressionType {
    const allExtensions: string[] = [];

    // get all extensions for the selected extension groups
    selectedExtensionGroupFilters.forEach((value, key) => {
        const extensionsGroup = Number(key);
        const extensions = getFileExtensionData(extensionsGroup).extensions;
        allExtensions.push(...extensions);
    });

    const searchExpression: SearchExpressionType = getSearchExpressionFromFileExtensions(
        allExtensions
    );
    return searchExpression;
}

function configureBroadExtensionsRestriction(
    selectedBroadextensionGroupFilter: FileExtensionType
): SearchExpressionType {
    const extensions: string[] = getFileExtensionData(selectedBroadextensionGroupFilter).extensions;
    const searchExpression: SearchExpressionType = getSearchExpressionFromFileExtensions(
        extensions
    );
    return searchExpression;
}

function getSearchExpressionFromFileExtensions(filesExtensions: string[]): SearchExpressionType {
    const fileTypeRestrictions: IsEqualTo[] = [];
    filesExtensions.forEach(fileExtension => {
        fileTypeRestrictions.push(
            isEqualType({
                Item: propertyUri({
                    FieldURI: 'FileExtension',
                }),
                FieldURIOrConstant: fieldURIOrConstantType({
                    Item: constantType({
                        Value: fileExtension,
                    }),
                }),
            })
        );
    });
    return orType({
        Items: fileTypeRestrictions,
    });
}

function configureAttachmentTypeRestriction(): SearchExpressionType {
    const modernAttachmentsFilter = isEqualType({
        Item: propertyUri({
            FieldURI: 'FileStorageProviderContext',
        }),
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constantType({
                Value: '6', // Value 6 indicates modern attachment
            }),
        }),
    });

    const classicAttachmentsFilter = isEqualType({
        Item: propertyUri({
            FieldURI: 'FileStorageProviderContext',
        }),
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constantType({
                Value: '2', // Value 2 indicates classic attachment
            }),
        }),
    });

    const linkAttachmentsFilter = isEqualType({
        Item: propertyUri({
            FieldURI: 'FileStorageProviderContext',
        }),
        FieldURIOrConstant: fieldURIOrConstantType({
            Item: constantType({
                Value: '5', // Value 5 indicates link attachment
            }),
        }),
    });

    return orType({
        Items: [modernAttachmentsFilter, classicAttachmentsFilter, linkAttachmentsFilter],
    });
}

export function getRestrictionsFromAttachmentListFilterState(
    filters: FilterState
): RestrictionType {
    const searchExpressions: SearchExpressionType[] = [];
    const broadExtensionsGroup = filters.broadExtensionGroupFilter.selectedValue;
    const extensionsGroup = filters.extensionGroupFilter.selectedValue;
    const dateRange = filters.dateRangeFilter.selectedValue;

    /*
        WHEN BUILDING THE DEFAULT RESTRICTIONS FOR FILESHUB/PHOTOHUB
        IT WILL HAVE AN IMPACT ON HOW WE WARMUP THE FILES FOLDER ON 1ST EXPERIENCE.
        IF ANYTHING CHANGES ON THE DEFAULT RESTRICTIONS THEN A CHANGE IS NEEDED AT
        Utah1\sources\dev\data\src\ApplicationLogic\Server\DefaultViewIndexer\PhotoHubFlightConfiguration.cs AND
        Utah1\sources\dev\data\src\ApplicationLogic\Server\DefaultViewIndexer\FilesHubFlightConfiguration.cs
    */

    // Images have a default size restriction to filter out signature images in emails
    const defaultSizeFilter =
        broadExtensionsGroup === FileExtensionType.Images ? DEFAULT_IMAGE_SIZE : null;
    const sizeFilter = filters.sizeFilter.selectedValue
        ? filters.sizeFilter.selectedValue
        : defaultSizeFilter;

    const photosOnlyFilter = filters.photosOnlyFilter.selectedValue;

    if (extensionsGroup.size > 0) {
        searchExpressions.push(configureExtensionsRestriction(extensionsGroup));
    } else if (broadExtensionsGroup) {
        /* only apply the broad extension group filters if specific ones are not chosen */
        searchExpressions.push(configureBroadExtensionsRestriction(broadExtensionsGroup));
    }

    if (dateRange.option !== null) {
        searchExpressions.push(configureDateRestriction(dateRange));
    }

    if (photosOnlyFilter) {
        searchExpressions.push(configurePhotosOnlyRestriction());
    }

    if (sizeFilter) {
        searchExpressions.push(configureFileSizeRestriction(sizeFilter));
    }

    searchExpressions.push(configureAttachmentTypeRestriction());

    if (searchExpressions.length > 0) {
        const bundledExpressions = andType({
            Items: searchExpressions,
        });

        return restrictionType({
            Item: bundledExpressions,
        });
    }

    return null;
}
